import { useEffect, useState } from 'react';
import { ArrowSquareOut, Check, CircleNotch, MagicWand } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import type { TemplateId, ThemeId, FontPairId } from '@/types/portfolio';
import { portfolioApi, type PortfolioState } from '@/lib/api/portfolio';
import { tenantApi } from '@/lib/api/tenant';
import { queryKeys } from '@/lib/api/queryKeys';
import { errorMessage } from '@/lib/api/errors';
import { publicPortfolioUrl } from '@/lib/config';
import { TEMPLATE_CATALOG, THEME_CATALOG, getTemplate, getTheme } from './catalog';
import { TemplateThumb } from './components/TemplateThumb';
import { TemplatePreview } from './components/TemplatePreview';

const FONT_PAIRS: ReadonlyArray<{
  id: FontPairId;
  name: string;
  description: string;
  sample: string;
}> = [
  {
    id: 'editorial',
    name: 'Editorial',
    description: 'Jakarta display · Newsreader italic accents',
    sample: 'Aa',
  },
  {
    id: 'technical',
    name: 'Technical',
    description: 'JetBrains Mono · Inter body',
    sample: 'Aa',
  },
  {
    id: 'humanist',
    name: 'Humanist',
    description: 'Inter display · Source Serif body',
    sample: 'Aa',
  },
  {
    id: 'brutal',
    name: 'Brutal',
    description: 'Archivo Black display · Inter body',
    sample: 'Aa',
  },
];

export function TemplatesPage() {
  const qc = useQueryClient();
  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: () => portfolioApi.get(),
    staleTime: 60_000,
  });
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    staleTime: 60_000,
  });

  const saved = portfolioQuery.data;
  const [template, setTemplate] = useState<TemplateId>('minimal');
  const [theme, setTheme] = useState<ThemeId>('ink');
  const [fontPair, setFontPair] = useState<FontPairId>('editorial');

  useEffect(() => {
    if (!saved) return;
    setTemplate(saved.template);
    setTheme(saved.theme);
    setFontPair(saved.fontPair);
  }, [saved]);

  const dirty =
    !!saved &&
    (saved.template !== template || saved.theme !== theme || saved.fontPair !== fontPair);

  const mutation = useMutation({
    mutationFn: () => portfolioApi.updateSettings({ template, theme, fontPair }),
    onSuccess: (next) => {
      qc.setQueryData<PortfolioState>(queryKeys.portfolio, next);
      toast.success('Template applied');
    },
    onError: (err) => {
      toast.error('Could not apply', { description: errorMessage(err, 'Try again.') });
    },
  });

  const templateMeta = getTemplate(template);
  const themeMeta = getTheme(theme);
  const liveUrl = tenantQuery.data?.subdomain
    ? publicPortfolioUrl(tenantQuery.data.subdomain)
    : '';

  return (
    <div className="mx-auto max-w-[1200px] space-y-10">
      <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-faint">
            Templates &amp; themes
          </p>
          <h1 className="mb-2 font-display text-3xl font-semibold tracking-tight">
            Reshape your site in a single click.
          </h1>
          <p className="max-w-xl font-editorial text-lg italic text-ink-muted">
            Every template carries your content across, untouched. Preview freely.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {liveUrl ? (
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-2 text-sm font-medium text-ink hover:bg-rail"
            >
              Preview live <ArrowSquareOut size={14} />
            </a>
          ) : null}
          <Button
            variant="primary"
            size="md"
            leadingIcon={
              mutation.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : dirty ? (
                <MagicWand size={14} />
              ) : (
                <Check size={14} />
              )
            }
            onClick={() => mutation.mutate()}
            disabled={!dirty || mutation.isPending || portfolioQuery.isPending}
          >
            {mutation.isPending ? 'Applying…' : dirty ? 'Apply changes' : 'Applied'}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_420px]">
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold tracking-wide">Template</h2>
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
              {TEMPLATE_CATALOG.length} available
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {TEMPLATE_CATALOG.map((t) => {
              const active = t.id === template;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplate(t.id)}
                  className={cn(
                    'group overflow-hidden rounded-xl border text-left transition-all',
                    active
                      ? 'border-ink shadow-float'
                      : 'border-line bg-board shadow-card hover:border-ink/40',
                  )}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <TemplateThumb id={t.id} />
                    {active ? (
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] text-board">
                        ✓
                      </span>
                    ) : null}
                  </div>
                  <div className="border-t border-line px-4 py-4">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-sm font-semibold text-ink">{t.name}</p>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                        {t.subtitle}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-ink-muted">{t.description}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {t.bestFor.map((b) => (
                        <span
                          key={b}
                          className="rounded-full border border-line bg-rail px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-muted"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h2 className="mb-4 text-sm font-semibold tracking-wide">Palette</h2>
              <div className="grid grid-cols-2 gap-3">
                {THEME_CATALOG.map((t) => {
                  const active = t.id === theme;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTheme(t.id)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border bg-board p-3 text-left transition-colors',
                        active ? 'border-ink' : 'border-line hover:border-ink/40',
                      )}
                    >
                      <span className="flex h-10 w-10 shrink-0 overflow-hidden rounded-md border border-line">
                        <span className="flex-1" style={{ background: t.colors[0] }} />
                        <span className="flex-1" style={{ background: t.colors[1] }} />
                        <span className="flex-1" style={{ background: t.colors[2] }} />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-ink">{t.name}</span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                          {t.mood}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h2 className="mb-4 text-sm font-semibold tracking-wide">Typography</h2>
              <div className="grid grid-cols-2 gap-3">
                {FONT_PAIRS.map((f) => {
                  const active = f.id === fontPair;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFontPair(f.id)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border bg-board p-3 text-left transition-colors',
                        active ? 'border-ink' : 'border-line hover:border-ink/40',
                      )}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line bg-rail font-display text-xl font-semibold">
                        {f.sample}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-ink">{f.name}</span>
                        <span className="block text-[11px] text-ink-faint">
                          {f.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <aside className="lg:sticky lg:top-4 lg:self-start">
          <div className="overflow-hidden rounded-2xl border border-line bg-board shadow-card">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">
                  Live preview
                </p>
                <p className="mt-0.5 text-sm font-semibold">{templateMeta.name}</p>
              </div>
              <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink-muted">
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    dirty ? 'bg-amber-500' : 'bg-emerald-500',
                  )}
                />
                {dirty ? 'Unsaved' : 'Applied'}
              </span>
            </div>
            <div className="aspect-[3/4] w-full overflow-hidden">
              <TemplatePreview template={template} />
            </div>
            <div className="grid grid-cols-3 gap-px bg-line text-center">
              <Meta label="Template" value={templateMeta.name} />
              <Meta label="Palette" value={themeMeta.name} />
              <Meta label="Type" value={fontPair} />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-board px-3 py-3">
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-faint">{label}</p>
      <p className="mt-1 text-xs font-semibold capitalize text-ink">{value}</p>
    </div>
  );
}
